import * as Sentry from "@sentry/capacitor";
import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { init as initReactSentry } from "@sentry/react";
import posthog from "posthog-js";
import {
  redactLogBody,
  sanitizeAnalyticsProperties,
  sanitizePath,
  sanitizePostHogVendorProperties,
  sanitizeSentryBreadcrumb,
  sanitizeSentryTransaction,
  sanitizeUrl,
  type AnalyticsProperties,
} from "./sanitize";

const DEFAULT_SENTRY_DSN =
  "https://19af0412200a5db2ca78e81e49ad214b@o4511153576214528.ingest.us.sentry.io/4511153578246144";
const DEFAULT_POSTHOG_KEY = "phc_tuabCaQuKsKZDpAozjVNZmLceJdiM9bZ3At2kGJtA2gp";
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN?.trim() || DEFAULT_SENTRY_DSN;
const SENTRY_ENVIRONMENT =
  import.meta.env.VITE_SENTRY_ENVIRONMENT?.trim() || import.meta.env.MODE;
const SENTRY_TRACES_SAMPLE_RATE = Number.parseFloat(
  import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || "0.1"
);
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY?.trim() || DEFAULT_POSTHOG_KEY;
const POSTHOG_HOST =
  import.meta.env.VITE_POSTHOG_HOST?.trim() || "https://us.i.posthog.com";
const POSTHOG_UI_HOST = import.meta.env.VITE_POSTHOG_UI_HOST?.trim();
const ANALYTICS_ENABLED = import.meta.env.VITE_ANALYTICS_ENABLED !== "false";
const POSTHOG_ERROR_TRACKING_ENABLED =
  import.meta.env.VITE_POSTHOG_ERROR_TRACKING_ENABLED !== "false";
const POSTHOG_LOGS_ENABLED = import.meta.env.VITE_POSTHOG_LOGS_ENABLED !== "false";
const RUNTIME_PLATFORM = Capacitor.isNativePlatform() ? "native" : "web";
const CLIENT_PLATFORM = Capacitor.getPlatform();
const APP_VERSION = typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : "0.0.0";
const APP_RELEASE = `tracker@${APP_VERSION}`;
const SENTRY_FLUSH_TIMEOUT_MS = 2000;
const LOCAL_WEB_HOSTNAMES = new Set(["localhost", "127.0.0.1", "::1"]);
const IS_LOCAL_HTTP_DEV =
  import.meta.env.DEV
  && typeof window !== "undefined"
  && window.location.protocol === "http:"
  && LOCAL_WEB_HOSTNAMES.has(window.location.hostname);
const SENTRY_ENABLED = Boolean(SENTRY_DSN) && !IS_LOCAL_HTTP_DEV;

let isInitialized = false;
let verifiedAnalyticsDistinctId: string | null = null;
const verifiedAnalyticsIdentityListeners = new Set<() => void>();

const publishVerifiedAnalyticsIdentity = (distinctId: string | null) => {
  if (verifiedAnalyticsDistinctId === distinctId) {
    return;
  }

  verifiedAnalyticsDistinctId = distinctId;

  // Copied, so a listener that unsubscribes itself while running doesn't mutate the set
  // mid-iteration. One throwing listener must not stop the rest from hearing about it.
  for (const listener of [...verifiedAnalyticsIdentityListeners]) {
    try {
      listener();
    } catch (error) {
      console.error("[Analytics] Verified identity listener failed", error);
    }
  }
};

const sanitizeIdentityValue = (value: unknown): string | undefined => {
  if (typeof value !== "string") {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed ? trimmed.slice(0, 120) : undefined;
};

/**
 * Person-property keys that would otherwise be dropped by the blocked-key pattern. They're
 * account facts we want to segment persons on, not free-text that could leak health data.
 */
const IDENTITY_ALLOWED_KEYS = ["verified_email"] as const;

const buildPostHogIdentityProperties = (
  userId: string,
  traits: AnalyticsProperties
) => {
  const safeTraits = sanitizeAnalyticsProperties(traits, {
    allowKeys: IDENTITY_ALLOWED_KEYS,
  });
  const email = sanitizeIdentityValue(traits.email);
  const name =
    sanitizeIdentityValue(traits.name) ||
    sanitizeIdentityValue(traits.fullName);

  return {
    ...safeTraits,
    supabase_user_id: userId,
    ...(email ? { email, supabase_user_email: email } : {}),
    ...(name ? { name, supabase_user_name: name } : {}),
  };
};

export const buildBaseProperties = () => ({
  app_environment: SENTRY_ENVIRONMENT,
  app_release: APP_RELEASE,
  client_platform: CLIENT_PLATFORM,
  runtime_platform: RUNTIME_PLATFORM,
});

const syncNativeAppContext = async () => {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  try {
    const appInfo = await CapacitorApp.getInfo();
    const nativeAppProperties = {
      native_app_build: appInfo.build,
      native_app_id: appInfo.id,
      native_app_version: appInfo.version,
    };

    if (SENTRY_ENABLED) {
      Sentry.setContext("native_app", nativeAppProperties);
      Sentry.setTags(nativeAppProperties);
    }

    if (POSTHOG_KEY) {
      posthog.register(nativeAppProperties);
    }
  } catch (error) {
    if (!SENTRY_ENABLED) {
      return;
    }

    Sentry.withScope((scope) => {
      scope.setContext("analytics_context", {
        source: "analytics_init",
        phase: "native_app_context",
      });
      Sentry.captureException(error);
    });
  }
};

const sanitizePostHogEvent = <
  TEvent extends { event?: unknown; properties?: AnalyticsProperties },
>(
  event: TEvent
): TEvent => {
  if (typeof event.event === "string" && event.event.startsWith("$")) {
    return {
      ...event,
      properties: sanitizePostHogVendorProperties(event.properties || {}),
    };
  }

  return {
    ...event,
    properties: sanitizeAnalyticsProperties(event.properties || {}),
  };
};

const addAnalyticsBreadcrumb = (
  message: string,
  properties: Record<string, unknown>
) => {
  if (!SENTRY_ENABLED || !ANALYTICS_ENABLED) {
    return;
  }

  Sentry.addBreadcrumb({
    category: "analytics",
    level: "info",
    message,
    data: properties,
  });
};

export const initializeAnalytics = () => {
  if (isInitialized || typeof window === "undefined") {
    return;
  }

  isInitialized = true;

  if (!ANALYTICS_ENABLED) {
    return;
  }

  if (SENTRY_ENABLED) {
    Sentry.init({
      dsn: SENTRY_DSN,
      enabled: true,
      enableNative: Capacitor.isNativePlatform(),
      enableNativeCrashHandling: true,
      enableWatchdogTerminationTracking: true,
      enableAppHangTracking: true,
      environment: SENTRY_ENVIRONMENT,
      release: APP_RELEASE,
      integrations: [Sentry.browserTracingIntegration()],
      tracesSampleRate: Number.isFinite(SENTRY_TRACES_SAMPLE_RATE)
        ? SENTRY_TRACES_SAMPLE_RATE
        : 0.1,
      ignoreErrors: [
        // Injected by Outlook/Office SafeLink crawlers and some in-app browsers, not our code:
        // "Object Not Found Matching Id:N, MethodName:update, ParamCount:N". Nothing to fix and
        // no user is affected — it only ever arrives as a non-Error promise rejection.
        /Object Not Found Matching Id/,
      ],
      beforeSend(event) {
        if (event.user?.email) {
          delete event.user.email;
        }

        if (event.request?.url) {
          event.request.url = sanitizeUrl(event.request.url);
        }

        // Error events are tagged with the current transaction name, i.e. the raw pathname.
        if (typeof event.transaction === "string" && event.transaction.startsWith("/")) {
          event.transaction = sanitizePath(event.transaction);
        }

        return event;
      },
      beforeSendTransaction(event) {
        return sanitizeSentryTransaction(event);
      },
      beforeBreadcrumb(breadcrumb) {
        return sanitizeSentryBreadcrumb(breadcrumb);
      },
    }, initReactSentry);

    Sentry.setTags(buildBaseProperties());
  }

  if (POSTHOG_KEY) {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      ui_host: POSTHOG_UI_HOST,
      autocapture: false,
      capture_pageleave: true,
      capture_pageview: true,
      defaults: "2025-05-24",
      // Session replay is permanently off: recordings of a fertility chart are health data.
      // There is deliberately no env flag to turn it back on.
      disable_session_recording: true,
      before_send: sanitizePostHogEvent,
      // Unhandled errors/rejections only. Console errors are picked up by the logs config
      // below, so capturing them here too would file every logged error twice.
      capture_exceptions: POSTHOG_ERROR_TRACKING_ENABLED && {
        capture_unhandled_errors: true,
        capture_unhandled_rejections: true,
        capture_console_errors: false,
      },
      logs: POSTHOG_LOGS_ENABLED
        ? {
            captureConsoleLogs: true,
            environment: SENTRY_ENVIRONMENT,
            serviceName: "tracker",
            serviceVersion: APP_VERSION,
            beforeSend: (record) => ({
              ...record,
              body: redactLogBody(record.body),
            }),
          }
        : undefined,
    });

    posthog.register(buildBaseProperties());
  }

  void syncNativeAppContext();

  trackEvent("app_opened", {
    path: sanitizePath(window.location.pathname),
  });
};

export const identifyAnalyticsUser = (
  userId: string,
  traits: AnalyticsProperties = {}
) => {
  if (!ANALYTICS_ENABLED || !isInitialized || !userId) {
    return;
  }

  const posthogIdentityProperties = buildPostHogIdentityProperties(
    userId,
    traits
  );

  if (SENTRY_ENABLED) {
    Sentry.setUser({ id: userId });
  }

  if (POSTHOG_KEY) {
    posthog.identify(userId, posthogIdentityProperties);
    posthog.register(posthogIdentityProperties);
  }
};

/**
 * Updates the current person's profile in PostHog without emitting a named event.
 *
 * Person properties only change when something is sent for that distinct id, so account
 * facts that arrive after login (subscription state, role, tracking setup) have to be pushed
 * explicitly — otherwise PostHog only knows what `identify` could see at sign-in and the
 * property never becomes filterable.
 *
 * `onceProperties` go through `$set_once`: written on first sight and never overwritten,
 * which is what first-touch values like the signup date want.
 */
export const setAnalyticsPersonProperties = (
  properties: AnalyticsProperties = {},
  onceProperties: AnalyticsProperties = {}
) => {
  if (!ANALYTICS_ENABLED || !isInitialized || !POSTHOG_KEY) {
    return;
  }

  const safeProperties = sanitizeAnalyticsProperties(properties, {
    allowKeys: IDENTITY_ALLOWED_KEYS,
  });
  const safeOnceProperties = sanitizeAnalyticsProperties(onceProperties, {
    allowKeys: IDENTITY_ALLOWED_KEYS,
  });

  if (
    Object.keys(safeProperties).length === 0
    && Object.keys(safeOnceProperties).length === 0
  ) {
    return;
  }

  posthog.setPersonProperties(safeProperties, safeOnceProperties);
};

/**
 * Server-signed identity for PostHog Support. `identify` alone is client-asserted, so
 * conversations fall back to scoping tickets to an anonymous per-browser widget session —
 * meaning the same person on a second device sees none of their own history, and tickets
 * aren't linkable to the account. Passing a distinct id plus its HMAC (signed in Postgres by
 * the custom access token hook and delivered as a `posthog_identity_hash` claim on the
 * session) makes PostHog trust the id and key ticket access off it instead.
 */
export const setVerifiedAnalyticsIdentity = (distinctId: string, hash: string) => {
  if (!ANALYTICS_ENABLED || !isInitialized || !POSTHOG_KEY || !distinctId || !hash) {
    return;
  }

  posthog.setIdentity(distinctId, hash);
  publishVerifiedAnalyticsIdentity(distinctId);
};

/**
 * The verified distinct id currently in effect, or `null` for anonymous scoping.
 *
 * Support conversations are resolved against one or the other — never both — so anything
 * fetched under the previous value belongs to a scope the new one can't see. Verification
 * lands asynchronously (it needs the session), so consumers have to react to it rather than
 * read it once at mount.
 */
export const getVerifiedAnalyticsDistinctId = () => verifiedAnalyticsDistinctId;

export const subscribeToVerifiedAnalyticsIdentity = (listener: () => void) => {
  verifiedAnalyticsIdentityListeners.add(listener);

  return () => {
    verifiedAnalyticsIdentityListeners.delete(listener);
  };
};

export const clearAnalyticsUser = () => {
  if (!ANALYTICS_ENABLED || !isInitialized) {
    return;
  }

  if (SENTRY_ENABLED) {
    Sentry.setUser(null);
  }

  if (POSTHOG_KEY) {
    // Before reset, so the verified identity doesn't outlive the session it belonged to.
    posthog.clearIdentity();
    posthog.reset();
  }

  publishVerifiedAnalyticsIdentity(null);
};

export const trackEvent = (
  name: string,
  properties: AnalyticsProperties = {}
) => {
  if (!ANALYTICS_ENABLED || !isInitialized || !name) {
    return;
  }

  const payload = {
    ...buildBaseProperties(),
    ...sanitizeAnalyticsProperties(properties),
  };

  addAnalyticsBreadcrumb(name, payload);

  if (POSTHOG_KEY) {
    posthog.capture(name, payload, { send_instantly: true });
  }
};

export const trackPageView = (pathname: string) => {
  if (!isInitialized) {
    return;
  }

  const path = sanitizePath(pathname);

  if (SENTRY_ENABLED) {
    Sentry.setTag("route", path);
  }

  trackEvent("page_viewed", { path });
};

export const trackAppLifecycle = async (isActive: boolean) => {
  if (!ANALYTICS_ENABLED || !isInitialized) {
    return;
  }

  const appState = isActive ? "foregrounded" : "backgrounded";

  if (SENTRY_ENABLED) {
    Sentry.setTag("app_state", appState);
  }

  trackEvent(isActive ? "app_foregrounded" : "app_backgrounded", {
    appState,
    path: typeof window === "undefined" ? undefined : sanitizePath(window.location.pathname),
  });

  if (!isActive && SENTRY_ENABLED) {
    await Sentry.flush(SENTRY_FLUSH_TIMEOUT_MS);
  }
};

export const captureException = (
  error: unknown,
  context: AnalyticsProperties = {}
) => {
  if (!ANALYTICS_ENABLED || !isInitialized) {
    return;
  }

  const safeContext = sanitizeAnalyticsProperties(context);

  if (SENTRY_ENABLED) {
    Sentry.withScope((scope) => {
      scope.setContext("analytics_context", safeContext);
      Sentry.captureException(error);
    });
  }

  // Handled errors are reported explicitly through here, so without this they'd only ever
  // reach Sentry — PostHog error tracking would see unhandled crashes and nothing else.
  if (POSTHOG_KEY && POSTHOG_ERROR_TRACKING_ENABLED) {
    posthog.captureException(error, {
      ...buildBaseProperties(),
      ...safeContext,
    });
  }
};

/**
 * The PostHog conversations (Support) API, or null when analytics are off entirely.
 *
 * We render our own chat UI against this rather than PostHog's bundled widget, so the
 * default launcher stays hidden — see useSupportChat.
 */
export const getSupportConversations = () => {
  if (!ANALYTICS_ENABLED || !isInitialized || !POSTHOG_KEY) {
    return null;
  }

  // Defensive: the extension is only present in posthog-js builds that bundle it, and
  // callers treat a null here as "fall back to email".
  return posthog.conversations ?? null;
};

/**
 * Structured app logs, forwarded to PostHog's logs product alongside captured console
 * output. Prefer this over a bare console call when the message is operationally useful
 * (sync outcomes, retry decisions) rather than local debugging noise.
 */
export const logMessage = (
  level: "debug" | "info" | "warn" | "error",
  message: string,
  attributes: AnalyticsProperties = {}
) => {
  if (!ANALYTICS_ENABLED || !isInitialized || !POSTHOG_KEY || !POSTHOG_LOGS_ENABLED) {
    return;
  }

  posthog.logger[level](redactLogBody(message), {
    ...buildBaseProperties(),
    ...sanitizeAnalyticsProperties(attributes),
  });
};

export type PostHogSurveyQuestionType =
  | "open"
  | "link"
  | "rating"
  | "single_choice"
  | "multiple_choice";

export interface PostHogSurveyQuestion {
  id?: string;
  type: PostHogSurveyQuestionType;
  question: string;
  description?: string | null;
  optional?: boolean;
  buttonText?: string;
  display?: "number" | "emoji";
  scale?: number;
  lowerBoundLabel?: string;
  upperBoundLabel?: string;
  choices?: string[];
  /** When true, the final entry in `choices` is an open-ended "Other" option. */
  hasOpenChoice?: boolean;
  branching?: {
    type: "next_question" | "end" | "specific_question" | "response_based" | string;
    index?: number;
    responseValues?: Record<string, unknown>;
  };
  link?: string | null;
}

export interface PostHogSurveyAppearance {
  backgroundColor?: string;
  textColor?: string;
  submitButtonColor?: string;
  submitButtonTextColor?: string;
  position?: string;
  whiteLabel?: boolean;
  thankYouMessageHeader?: string;
  thankYouMessageDescription?: string;
}

export interface PostHogSurvey {
  id: string;
  name: string;
  description?: string;
  type?: "popover" | "button" | "api" | "widget" | string;
  questions: PostHogSurveyQuestion[];
  appearance?: PostHogSurveyAppearance;
  conditions?: Record<string, unknown>;
  current_iteration?: number;
  current_iteration_start_date?: string;
}

/**
 * Fetches active matching surveys configured in PostHog.
 */
export const getActivePostHogSurveys = (
  callback: (surveys: PostHogSurvey[]) => void,
  forceReload = false
) => {
  if (!ANALYTICS_ENABLED || !isInitialized || !POSTHOG_KEY) {
    callback([]);
    return;
  }

  const ph = posthog as unknown as {
    getActiveMatchingSurveys?: (cb: (surveys: PostHogSurvey[]) => void, reload?: boolean) => void;
    getSurveys?: (cb: (surveys: PostHogSurvey[]) => void, reload?: boolean) => void;
  };

  if (typeof ph.getActiveMatchingSurveys === "function") {
    ph.getActiveMatchingSurveys(callback, forceReload);
  } else if (typeof ph.getSurveys === "function") {
    ph.getSurveys(callback, forceReload);
  } else {
    callback([]);
  }
};

/**
 * Captures a 'survey shown' event in PostHog.
 */
export const trackSurveyShown = (
  survey: { id: string; name: string; current_iteration?: number },
  extraProperties: AnalyticsProperties = {}
) => {
  if (!ANALYTICS_ENABLED || !isInitialized || !POSTHOG_KEY) return;

  trackEvent("survey shown", {
    $survey_id: survey.id,
    $survey_name: survey.name,
    $survey_iteration: survey.current_iteration,
    ...extraProperties,
  });
};

/**
 * Captures a 'survey sent' response event in PostHog.
 */
/**
 * Builds the `survey sent` property payload PostHog's survey UI can read.
 *
 * PostHog does NOT display arbitrary property keys as survey responses. It
 * looks for:
 *   - `$survey_response_<questionId>` — the modern, id-keyed answer
 *   - `$survey_questions` — [{ id, question, response_key }], used to label
 *     each answer in the responses table
 *   - `$survey_response` / `$survey_response_<n>` — the legacy positional keys,
 *     still read as a fallback for events sent before ids existed
 *
 * We emit all three so responses render regardless of which path the UI takes.
 * `responses` comes in keyed by question id (or `q_<index>` when a question has
 * no id), exactly as the survey modal stores it.
 */
export const buildSurveyResponsePayload = (
  questions: PostHogSurveyQuestion[],
  responses: Record<string, unknown>
): AnalyticsProperties => {
  const payload: AnalyticsProperties = {};
  const surveyQuestions: Array<{
    id?: string;
    question: string;
    response_key: string;
  }> = [];

  const legacyEntries: Array<[string, unknown]> = [];

  questions.forEach((question, index) => {
    const answerKey = question.id ?? `q_${index}`;
    // Unanswered optional questions are sent as empty rather than omitted, so
    // the responses table keeps a cell for every question.
    const value = responses[answerKey] ?? "";
    // Positional key: `$survey_response` for the first question, then indexed.
    const legacyKey = index === 0 ? "$survey_response" : `$survey_response_${index}`;
    const responseKey = question.id ? `$survey_response_${question.id}` : legacyKey;

    surveyQuestions.push({
      id: question.id,
      question: question.question,
      response_key: responseKey,
    });

    payload[responseKey] = value;
    legacyEntries.push([legacyKey, value]);
  });

  // Legacy keys are written last and never overwrite an id-keyed answer — a
  // numeric question id such as "1" produces the same key as index 1's
  // positional fallback, and the id-keyed value is the authoritative one.
  for (const [key, value] of legacyEntries) {
    if (!(key in payload)) payload[key] = value;
  }

  payload.$survey_questions = surveyQuestions;
  return payload;
};

export const trackSurveySent = (
  survey: {
    id: string;
    name: string;
    current_iteration?: number;
    questions?: PostHogSurveyQuestion[];
  },
  responses: Record<string, unknown>,
  extraProperties: AnalyticsProperties = {}
) => {
  if (!ANALYTICS_ENABLED || !isInitialized || !POSTHOG_KEY) return;

  trackEvent("survey sent", {
    $survey_id: survey.id,
    $survey_name: survey.name,
    $survey_iteration: survey.current_iteration,
    $survey_completed: true,
    ...buildSurveyResponsePayload(survey.questions ?? [], responses),
    // Lets PostHog suppress the survey for this person on other devices.
    $set: { [`$survey_responded/${survey.id}`]: true },
    ...extraProperties,
  });
};

/**
 * Captures a 'survey dismissed' event in PostHog.
 */
export const trackSurveyDismissed = (
  survey: { id: string; name: string; current_iteration?: number },
  extraProperties: AnalyticsProperties = {}
) => {
  if (!ANALYTICS_ENABLED || !isInitialized || !POSTHOG_KEY) return;

  trackEvent("survey dismissed", {
    $survey_id: survey.id,
    $survey_name: survey.name,
    $survey_iteration: survey.current_iteration,
    ...extraProperties,
  });
};

